import { forwardRef, type ButtonHTMLAttributes } from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn.js";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 rounded-pill font-sans font-semibold transition-colors duration-220 ease-out-quart disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      variant: {
        primary:   "bg-accent text-accent-on hover:brightness-105",
        secondary: "bg-surface-2 text-ink hover:bg-rule",
        ghost:     "bg-transparent text-ink-soft hover:bg-surface-2 hover:text-ink",
        danger:    "bg-risk text-bg hover:brightness-105",
        system:    "bg-system text-bg hover:brightness-105",
        outline:   "border border-rule bg-transparent text-ink hover:bg-surface-2",
      },
      size: {
        sm: "h-8 px-3 text-body",
        md: "h-9 px-4 text-body",
        lg: "h-11 px-5 text-body-lg",
        icon: "size-9 p-0",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { buttonVariants };
